import {
  AbstractUserService,
  CreateInviteDto,
  DashboardIndicators,
  FindAllUserRequestDto,
  PapelOption,
  UpdateUserDto,
  UserResponse,
} from '@aionzdev/modules';
import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Usuario, UsuarioEmpresa } from '../models/usuario';
import { UserApiService } from './api/user-api.service';
import { CompanyContextService } from './company-context.service';

@Injectable({ providedIn: 'root' })
export class UserService extends AbstractUserService {
  private readonly _api = inject(UserApiService);
  private readonly _companyContext = inject(CompanyContextService);

  private get empresaId(): number {
    return this._companyContext.companyId() ?? 0;
  }

  override findAllUsers(data: FindAllUserRequestDto): Observable<HttpResponse<UserResponse[]>> {
    return this._api.findAllUsers(this.empresaId, {
      paginaAtual: data.currentPage,
      registrosPorPagina: data.take,
      search: data.search,
    }).pipe(
      map(res => new HttpResponse({
        body: (res.body ?? []).map(u => this.toUserResponseFromEmpresa(u)),
        headers: new HttpHeaders({ count: res.headers.get('X-Total-Count') ?? '0' }),
      }))
    );
  }

  override findById(id: string): Observable<UserResponse> {
    return this._api.findById(Number(id)).pipe(map(u => this.toUserResponse(u)));
  }

  override update(data: UpdateUserDto): Observable<UserResponse> {
    return this._api.update(data).pipe(map(u => this.toUserResponse(u)));
  }

  override getDashboardIndicators(): Observable<DashboardIndicators> {
    return this._api.getDashboardIndicators(this.empresaId);
  }

  override deactivateAccountAdmin(id: string): Observable<boolean> {
    return this._api.deactivateAccountAdmin(id);
  }

  override findByCNPJ(cnpj: string): Observable<UserResponse | null> {
    return this._api.findByCNPJ(cnpj).pipe(
      map(u => (u ? this.toUserResponse(u) : null)),
      catchError(() => of(null))
    );
  }

  override export(): Observable<{ message: string }> {
    return this._api.export();
  }

  override createInvite(data: CreateInviteDto): Observable<unknown> {
    return this._api.createInvite(this.empresaId, data);
  }

  override getAvailablePapeis(): Observable<PapelOption[]> {
    return this._api.getAvailablePapeis(this.empresaId);
  }

  private toUserResponseFromEmpresa(u: UsuarioEmpresa): UserResponse {
    return {
      id: String(u.id),
      name: u.name,
      userName: u.email ?? '',
      email: u.email ?? undefined,
      cpf: u.cpf ?? undefined,
      phone: u.phone ?? undefined,
      avatar: u.avatar ?? undefined,
      isDeleted: u.isDeleted,
      lastAccessAt: u.lastAccessAt ? new Date(u.lastAccessAt) : undefined,
      createdAt: new Date(u.createdAt),
      updatedAt: new Date(u.updatedAt),
      papel: u.papel,
    };
  }

  private toUserResponse(u: Usuario): UserResponse {
    return {
      id: String(u.id),
      email: u.email,
      cpf: u.cpf || u.empresaPadrao?.cnpj,
      name: u.nome,
      userName: u.email,
      avatar: u.avatar,
      phone: u.telefone1 || u.telefone2,
      lastAccessAt: undefined,
      isDeleted: false,
      tenantId: undefined,
      createdAt: new Date(),
      updatedAt: new Date(),
      groupsName: [],
      stateRegistration: u.empresaPadrao?.ie,
      userAttributes: [{ key: 'bio', value: u.bio ?? '', label: 'Biografia' }]
    };
  }
}
